import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import toast from 'react-hot-toast';
import { Connection, ConnectionTestResult } from '../../types/connection';
import { Partner } from '../../types/partner';
import { Task, TaskExecution } from '../../types/task';
import { StatusIndicator, StatusType } from '../shared/StatusIndicators';
// THUB-280: toProvisioningStatus replaces `(x || 'active') as any` -- the cast is
// what let an invalid (present-but-wrong) status reach the badge silently.
import { ResourceStatusBadge, toProvisioningStatus } from '../shared/StatusBadge';
import { connectionsApi, DependencyError, Dependency } from '../../services/api/connections';
import EditConnection from '../EditConnection';
import { TaskDetails } from '../tasks/TaskDetails';
import { TaskEditor } from '../tasks/TaskEditor';
import { tasksApi } from '../../services/api/tasks';
import { useTaskActions } from '../../hooks/useTaskActions';
import { UpdateTaskRequest, TransferDirection } from '../../types/task';
import { ConnectionTestButton } from './ConnectionTestButton';
import { TestResultPanel } from './TestResultPanel';
import { TestHistoryTable } from './TestHistoryTable';
import { CascadeDeleteDialog } from '../shared/CascadeDelete';

// Stable empty reference: `?? []` would be a new array every render.
const NO_TASKS: Task[] = [];
import { AuditLogViewer } from '../audit';
import { RoleGuard } from '../RoleGuard';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';
// Version conflict temporarily disabled for debugging - causes ErrorBoundary crash
// import { useVersionConflict } from '../../hooks/useVersionConflict';
// CSS import removed - using Tailwind classes

// Simple task list component for use in connection modal
interface ConnectionTaskListProps {
  tasks: Task[];
  onTaskSelect: (task: Task) => void;
}

function ConnectionTaskList({ tasks, onTaskSelect }: ConnectionTaskListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Task>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getStatusColor = (status: string): StatusType => {
    switch (status) {
      case 'enabled': return 'connected';
      case 'running': return 'info';
      case 'completed': return 'connected';
      case 'failed': return 'error';
      case 'disabled': return 'neutral';
      default: return 'info';
    }
  };

  // Filter, search, and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...tasks];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(search) ||
        t.partnerName.toLowerCase().includes(search) ||
        t.status.toLowerCase().includes(search) ||
        t.transferDirection.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Number comparison
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [tasks, searchTerm, sortField, sortOrder]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (field: keyof Task) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: keyof Task) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-4">
      {/* Search and controls */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="
              w-full px-4 py-2.5 pr-10
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-lg text-sm
              text-neutral-900 dark:text-slate-100
              placeholder:text-neutral-400 dark:placeholder:text-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="
                absolute right-3 top-1/2 -translate-y-1/2
                text-neutral-400 hover:text-neutral-600 dark:hover:text-slate-300
                text-xl leading-none
              "
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 dark:bg-slate-800 border-b border-neutral-200 dark:border-slate-700">
              <th
                className="
                  px-4 py-3 text-left font-semibold
                  text-neutral-700 dark:text-slate-200
                  cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-700
                  transition-colors duration-150
                "
                onClick={() => handleSort('name')}
              >
                Task Name {getSortIcon('name')}
              </th>
              <th
                className="
                  px-4 py-3 text-left font-semibold
                  text-neutral-700 dark:text-slate-200
                  cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-700
                  transition-colors duration-150
                "
                onClick={() => handleSort('transferDirection')}
              >
                Direction {getSortIcon('transferDirection')}
              </th>
              <th
                className="
                  px-4 py-3 text-left font-semibold
                  text-neutral-700 dark:text-slate-200
                  cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-700
                  transition-colors duration-150
                "
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-200">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800">
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-neutral-500 dark:text-slate-400 italic"
                >
                  {searchTerm ? 'No tasks match your search' : 'No tasks found'}
                </td>
              </tr>
            ) : (
              paginatedData.map(task => (
                <tr
                  key={task.taskId}
                  onClick={() => onTaskSelect(task)}
                  className="
                    border-b border-neutral-100 dark:border-slate-700/50
                    hover:bg-primary-50 dark:hover:bg-primary-900/20
                    cursor-pointer transition-colors duration-150
                  "
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-neutral-900 dark:text-slate-100">
                        {task.name}
                      </span>
                      {task.configuration?.sourceDirectory && (
                        <span className="text-xs text-neutral-500 dark:text-slate-400 mt-0.5">
                          {task.configuration.sourceDirectory}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="
                      inline-block px-2 py-1 rounded text-xs font-medium
                      bg-neutral-100 dark:bg-slate-700
                      text-neutral-700 dark:text-slate-300
                    ">
                      {task.transferDirection}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator
                      status={getStatusColor(task.status)}
                      label={task.status}
                      size="small"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      className="
                        px-3 py-1.5 text-xs font-medium rounded
                        bg-primary-600 hover:bg-primary-700
                        text-white
                        transition-colors duration-150
                      "
                      onClick={(e) => {
                        e.stopPropagation();
                        onTaskSelect(task);
                      }}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <div className="text-sm text-neutral-600 dark:text-slate-400">
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredAndSortedData.length)} to{' '}
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} tasks
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏮
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ◀
            </button>

            <span className="px-3 py-1.5 text-sm text-neutral-600 dark:text-slate-400">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ▶
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(Math.max(1, totalPages))}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏭
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <label className="text-neutral-600 dark:text-slate-400">Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="
                px-2 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                focus:outline-none focus:ring-2 focus:ring-primary-500
                cursor-pointer
              "
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span className="text-neutral-600 dark:text-slate-400">per page</span>
          </div>
        </div>
      )}
    </div>
  );
}

interface ConnectionDetailsProps {
  connection: Connection;
  partners: Partner[];
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

type TabType = 'overview' | 'configuration' | 'health' | 'history' | 'tasks' | 'audit';

function ConnectionDetails({ connection, partners, onClose, onUpdate }: ConnectionDetailsProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCascadeDialog, setShowCascadeDialog] = useState(false);
  const [cascadeDependencies, setCascadeDependencies] = useState<Dependency[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  // THUB-299: was `connectionTasks` / `dependenciesLoading` in useState, filled by a mount
  // effect. Keyed under `tasks` so App.tsx's coalesced invalidation reaches it -- see the
  // THUB-182 note further down, whose entire workaround this removes the need for.
  const tasksQuery = useQuery({
    queryKey: queryKeys.tasks.byConnection(connection.connectionId),
    queryFn: () => connectionsApi.getConnectionTasks(connection.connectionId),
  });
  // A failed dependency read must read as "no known dependencies" and never blank the modal,
  // which is what the original's catch-and-fall-back-to-[] did.
  const connectionTasks: Task[] = tasksQuery.data ?? NO_TASKS;
  const dependenciesLoading = tasksQuery.isPending;

  // 018-thub-5: Key rotation state
  const [isRotatingKey, setIsRotatingKey] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  // 021-pgp-encryption: PGP key upload state
  const [showPgpUpload, setShowPgpUpload] = useState(false);
  const [pgpPublicKey, setPgpPublicKey] = useState('');
  const [pgpPrivateKey, setPgpPrivateKey] = useState('');
  const [pgpPassphrase, setPgpPassphrase] = useState('');
  const [pgpUploading, setPgpUploading] = useState(false);
  const [pgpError, setPgpError] = useState<string | null>(null);

  // Nested modal states
  // THUB-162: the connection-task list object is a partial Task (its mapper
  // drops deliveryMode/prefix/snsTopicArn/integration and never merges
  // integration at all), so rendering TaskDetails off it showed a stale
  // "still provisioning" guide and dropped the Schedule card. Open the modal,
  // fetch the FULL task, render that.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [fullTask, setFullTask] = useState<Task | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  // THUB-161: feed the History tab + Overview metrics on the Connection surface
  // too (was showing "0 executions" regardless of run history).
  const [taskExecutions, setTaskExecutions] = useState<TaskExecution[]>([]);

  // THUB-231: the task Edit action opens a nested <TaskEditor>, mirroring
  // TaskList. Kept as local modal state (not in useTaskActions) because it is
  // UI state, not an API call.
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  const closeTaskDetail = useCallback(() => {
    setSelectedTaskId(null);
    setFullTask(null);
    setTaskDetailLoading(false);
    setTaskExecutions([]);
  }, []);

  const openTaskDetail = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setFullTask(null);
    setTaskExecutions([]);
    setTaskDetailLoading(true);
    tasksApi
      .getTask(taskId)
      .then(detail => {
        setFullTask(detail);
      })
      .catch(err => {
        console.error('Failed to load task detail:', err);
        toast.error('Could not load task details. Please try again.');
        closeTaskDetail();
      })
      .finally(() => {
        setTaskDetailLoading(false);
      });
    // Executions load independently (best-effort) for the History tab.
    tasksApi
      .getExecutionsForTask(taskId, 50)
      .then(setTaskExecutions)
      .catch(err => {
        console.error('Failed to load task executions:', err);
        setTaskExecutions([]);
      });
  }, [closeTaskDetail]);

  // Version conflict handling - temporarily disabled for debugging
  // const {
  //   hasConflict,
  //   conflictState,
  //   startTracking,
  //   stopTracking,
  //   refresh: refreshVersion,
  //   clearConflict,
  // } = useVersionConflict({
  //   entityType: 'connection',
  //   onConflict: (state) => {
  //     console.log('Connection version conflict detected:', state);
  //   },
  //   onExternalUpdate: (version) => {
  //     console.log('Connection updated externally:', version);
  //   },
  // });

  // Temporary stub values while version conflict is disabled (kept for future re-enablement)

  // State for showing conflict toast - disabled
  // const [showConflictToast, setShowConflictToast] = useState(false);


  // Track connection version for conflict detection - temporarily disabled for debugging
  // useEffect(() => {
  //   try {
  //     if (connection.connectionId && typeof connection.version === 'number') {
  //       startTracking(connection.connectionId, {
  //         version: connection.version,
  //         updatedAt: connection.updatedAt || new Date().toISOString(),
  //         updatedBy: undefined, // Connection type doesn't have updatedBy field
  //       });
  //     }
  //   } catch (error) {
  //     console.warn('Failed to start version tracking for connection:', error);
  //   }
  //   return () => {
  //     try {
  //       stopTracking();
  //     } catch (error) {
  //       console.warn('Failed to stop version tracking:', error);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [connection.connectionId, connection.version]);

  // Show conflict toast when conflict detected - temporarily disabled
  // useEffect(() => {
  //   if (hasConflict) {
  //     setShowConflictToast(true);
  //   }
  // }, [hasConflict]);

  // Refresh function for manual updates. The read itself is the query's job now; this only
  // adds the parent notification the non-preserveState callers depend on.
  const refreshDependencies = useCallback(async (preserveState: boolean = true) => {
    await tasksQuery.refetch();
    if (!preserveState) {
      onUpdate();
    }
  }, [tasksQuery, onUpdate]);

  // Version conflict refresh handler - temporarily disabled for debugging
  // const handleConflictRefresh = useCallback(async () => {
  //   setShowConflictToast(false);
  //   clearConflict();
  //   await onUpdate();
  // }, [clearConflict, onUpdate]);

  // THUB-299: THUB-182 subscribed to task_status_changes / execution_updates here and re-ran a
  // local fetch through useCoalescedCallback, because -- in its own words -- this modal's task
  // list lived in component-local useState, "entirely outside TanStack", so App.tsx's
  // invalidateQueries could never reach it and the open modal went stale whenever a task changed
  // elsewhere.
  //
  // The list is now a query keyed under `tasks`, and App.tsx already subscribes to those same two
  // topics and flushes queryKeys.tasks.all coalesced onto one trailing edge. So the reach is
  // native and the coalescing is central -- the local subscription would only duplicate both.

  // THUB-231: real task actions. These callbacks used to be console.log stubs,
  // so confirming a task deletion here silently did nothing.
  const taskActions = useTaskActions({
    onChanged: () => refreshDependencies(true),
  });

  // THUB-231: save handler for the nested <TaskEditor> (Edit action).
  const handleUpdateTask = useCallback(async (taskId: string, taskData: UpdateTaskRequest) => {
    try {
      await tasksApi.updateTask(taskId, taskData);
      setTaskToEdit(null);
      closeTaskDetail();
      toast.success('Task update in progress');
      await refreshDependencies(true);
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error; // let TaskEditor surface the error inline
    }
  }, [closeTaskDetail, refreshDependencies]);

  const handleValidateTaskConfiguration = useCallback(async (
    transferDirection: TransferDirection,
    configuration: any
  ) => {
    try {
      return await tasksApi.validateTaskConfiguration(transferDirection, configuration);
    } catch (error) {
      console.warn('Configuration validation unavailable:', error);
      return {
        valid: true,
        errors: ['Configuration validation unavailable due to network issues'],
      };
    }
  }, []);

  // Check if connection has dependencies that prevent deletion (use loaded data)
  const hasTasks = connectionTasks.length > 0;

  // Attempt to delete connection (without cascade first)
  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await connectionsApi.deleteConnection(connection.connectionId, { version: connection.version });
      setShowDeleteConfirm(false);

      // Close modal and show success message
      // Manual refresh will be required to see the connection removed from the list
      onClose();
    } catch (err) {
      // Handle DependencyError - show cascade dialog
      if (err instanceof DependencyError) {
        setCascadeDependencies(err.dependencies);
        setShowDeleteConfirm(false);
        setShowCascadeDialog(true);
        setIsDeleting(false);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to delete connection';
      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  // Handle cascade delete confirmation
  const handleCascadeDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await connectionsApi.deleteConnection(connection.connectionId, { cascade: true, version: connection.version });
      setShowCascadeDialog(false);

      // Close modal - manual refresh will be required to see the changes
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete connection';
      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'configuration', label: 'Configuration' },
    { id: 'health', label: 'Test & Diagnostics' },
    { id: 'history', label: 'History' },
    { id: 'tasks', label: 'Tasks', count: connectionTasks.length },
    { id: 'audit', label: 'Audit History' },
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Basic Information
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Connection Name
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.name}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Connection ID
            </label>
            <span className="text-sm font-mono text-neutral-600 dark:text-slate-400">
              {connection.connectionId}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Partner
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.partnerName}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Created By
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.createdBy}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Hosting Type
            </label>
            <span className="
              inline-block w-fit px-2 py-1 rounded text-xs font-medium
              bg-primary-100 dark:bg-primary-900/30
              text-primary-700 dark:text-primary-300
            ">
              {connection.hostingType}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Transfer Method
            </label>
            <span className="
              inline-block w-fit px-2 py-1 rounded text-xs font-medium
              bg-purple-100 dark:bg-purple-900/30
              text-purple-700 dark:text-purple-300
            ">
              {connection.transferMethod}
            </span>
          </div>
        </div>
      </div>

      {/* Egress IPs - shown for remote SFTP and HTTP connections */}
      {connection.connectorEgressIps && connection.connectorEgressIps.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-amber-800 dark:text-amber-200
            pb-3 mb-4 border-b border-amber-200 dark:border-amber-700
          ">
            Firewall Whitelist IPs
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            The partner must whitelist these IP addresses for TransferHub to connect to their server.
          </p>
          <div className="flex flex-wrap gap-2">
            {connection.connectorEgressIps.map((ip) => (
              <span
                key={ip}
                className="
                  inline-block px-3 py-1.5 rounded-md text-sm font-mono font-medium
                  bg-amber-100 dark:bg-amber-900/40
                  text-amber-800 dark:text-amber-200
                  border border-amber-300 dark:border-amber-700
                  select-all
                "
              >
                {ip}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Status */}
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Status
        </h4>
        <div className="flex gap-8">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Connection Status
            </label>
            <ResourceStatusBadge
              status={toProvisioningStatus(connection.provisioningStatus)}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* SSH Key Status - Hosted SFTP connections only */}
      {connection.hostingType === 'hosted' && connection.transferMethod === 'sftp' && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            SSH Authentication
          </h4>
          {connection.config.publicKeyContent ? (
            <div className="
              flex items-center gap-3 p-4 rounded-lg
              bg-success-50 dark:bg-success-900/20
              border border-success-200 dark:border-success-800
            ">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" className="text-success-500 flex-shrink-0">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-success-800 dark:text-success-200">SSH Key Configured</p>
                <p className="text-xs text-success-600 dark:text-success-400 mt-0.5">
                  Partner can authenticate via SSH public key
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="
                flex items-start gap-3 p-4 rounded-lg
                bg-amber-50 dark:bg-amber-900/20
                border border-amber-200 dark:border-amber-800
              ">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-500 flex-shrink-0 mt-0.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="9" x2="12" y2="13" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="17" x2="12.01" y2="17" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">SSH Public Key Required</p>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    Partner cannot connect via SFTP until an SSH public key is registered.
                    The partner must provide their public key so it can be added to the connection.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium
                  bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200
                  border border-amber-300 dark:border-amber-700
                  hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors
                "
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Add SSH Public Key
              </button>
            </div>
          )}
        </div>
      )}

      {/* PGP Encryption */}
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          PGP Encryption
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Public Key
            </label>
            {connection.pgpPublicKeyConfigured ? (
              <span className="inline-block w-fit px-2 py-1 rounded text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
                Configured
              </span>
            ) : (
              <span className="inline-block w-fit px-2 py-1 rounded text-xs font-medium bg-neutral-100 dark:bg-slate-700 text-neutral-500 dark:text-slate-400">
                Not Configured
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Private Key
            </label>
            {connection.pgpPrivateKeyConfigured ? (
              <span className="inline-block w-fit px-2 py-1 rounded text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
                Configured
              </span>
            ) : (
              <span className="inline-block w-fit px-2 py-1 rounded text-xs font-medium bg-neutral-100 dark:bg-slate-700 text-neutral-500 dark:text-slate-400">
                Not Configured
              </span>
            )}
          </div>
          {connection.pgpKeyFingerprint && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Key Fingerprint
              </label>
              <span className="text-sm font-mono text-neutral-600 dark:text-slate-400 break-all">
                {connection.pgpKeyFingerprint}
              </span>
            </div>
          )}
          {connection.pgpKeyExpiration && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Key Expiration
              </label>
              <span className={`text-sm ${
                connection.pgpKeyWarning
                  ? 'text-warning-600 dark:text-warning-400 font-medium'
                  : 'text-neutral-900 dark:text-slate-100'
              }`}>
                {formatDateTime(connection.pgpKeyExpiration, activeTimezone(), 'dateOnly')}
              </span>
            </div>
          )}
        </div>
        {connection.pgpKeyWarning && (
          <div className="mt-3 p-3 rounded-lg text-sm bg-warning-50 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200 border border-warning-200 dark:border-warning-800">
            {connection.pgpKeyWarning}
          </div>
        )}
        <div className="mt-4">
          <button
            type="button"
            className="
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium
              bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300
              border border-primary-200 dark:border-primary-800
              hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors
            "
            onClick={() => {
              setShowPgpUpload(!showPgpUpload);
              setPgpError(null);
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            {showPgpUpload ? 'Cancel Upload' : (connection.pgpPublicKeyConfigured || connection.pgpPrivateKeyConfigured) ? 'Update PGP Keys' : 'Upload PGP Keys'}
          </button>
        </div>

        {/* PGP Key Upload Form */}
        {showPgpUpload && (
          <div className="mt-4 p-4 bg-white dark:bg-slate-900 border border-neutral-200 dark:border-slate-700 rounded-lg space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-neutral-700 dark:text-slate-300">
                PGP Public Key
              </label>
              <textarea
                value={pgpPublicKey}
                onChange={(e) => setPgpPublicKey(e.target.value)}
                placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----&#10;...&#10;-----END PGP PUBLIC KEY BLOCK-----"
                rows={6}
                className="
                  w-full px-3 py-2 rounded-lg text-sm font-mono
                  bg-neutral-50 dark:bg-slate-800
                  border border-neutral-300 dark:border-slate-600
                  text-neutral-900 dark:text-slate-100
                  placeholder:text-neutral-400 dark:placeholder:text-slate-500
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  resize-y
                "
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-neutral-700 dark:text-slate-300">
                PGP Private Key
              </label>
              <textarea
                value={pgpPrivateKey}
                onChange={(e) => setPgpPrivateKey(e.target.value)}
                placeholder="-----BEGIN PGP PRIVATE KEY BLOCK-----&#10;...&#10;-----END PGP PRIVATE KEY BLOCK-----"
                rows={6}
                className="
                  w-full px-3 py-2 rounded-lg text-sm font-mono
                  bg-neutral-50 dark:bg-slate-800
                  border border-neutral-300 dark:border-slate-600
                  text-neutral-900 dark:text-slate-100
                  placeholder:text-neutral-400 dark:placeholder:text-slate-500
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                  resize-y
                "
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-neutral-700 dark:text-slate-300">
                Private Key Passphrase
              </label>
              <input
                type="password"
                value={pgpPassphrase}
                onChange={(e) => setPgpPassphrase(e.target.value)}
                placeholder="Enter passphrase (if key is password-protected)"
                className="
                  w-full px-3 py-2 rounded-lg text-sm
                  bg-neutral-50 dark:bg-slate-800
                  border border-neutral-300 dark:border-slate-600
                  text-neutral-900 dark:text-slate-100
                  placeholder:text-neutral-400 dark:placeholder:text-slate-500
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                "
              />
            </div>
            {pgpError && (
              <div className="p-3 rounded-lg text-sm bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400 border border-danger-200 dark:border-danger-800">
                {pgpError}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                disabled={pgpUploading || (!pgpPublicKey.trim() && !pgpPrivateKey.trim())}
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-primary-600 hover:bg-primary-700 text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                onClick={async () => {
                  setPgpUploading(true);
                  setPgpError(null);
                  try {
                    const pgpData: any = {};
                    if (pgpPublicKey.trim()) pgpData.pgpPublicKey = pgpPublicKey.trim();
                    if (pgpPrivateKey.trim()) pgpData.pgpPrivateKey = pgpPrivateKey.trim();
                    if (pgpPassphrase.trim()) pgpData.pgpPrivateKeyPassphrase = pgpPassphrase.trim();
                    const updatedConnection = await connectionsApi.uploadPgpKeys(connection.connectionId, pgpData);
                    const fingerprint = updatedConnection.pgpKeyFingerprint;
                    toast.success(fingerprint
                      ? `PGP key uploaded successfully. Fingerprint: ${fingerprint}`
                      : 'PGP key uploaded successfully'
                    );
                    if (updatedConnection.pgpKeyWarning) {
                      toast(updatedConnection.pgpKeyWarning, { icon: '\u26A0\uFE0F', duration: 6000 });
                    }
                    setShowPgpUpload(false);
                    setPgpPublicKey('');
                    setPgpPrivateKey('');
                    setPgpPassphrase('');
                    onUpdate();
                  } catch (err: any) {
                    const code = err.code || err.details?.errorCode;
                    if (code === 'PGP_KEY_INVALID') {
                      setPgpError('The PGP key is invalid. Please check the key format and try again.');
                    } else if (code === 'PGP_KEY_EXPIRED') {
                      setPgpError('The PGP key has expired. Please upload a valid, non-expired key.');
                    } else {
                      setPgpError(err.message || 'Failed to upload PGP keys');
                    }
                  } finally {
                    setPgpUploading(false);
                  }
                }}
              >
                {pgpUploading ? 'Uploading...' : 'Upload Keys'}
              </button>
              <button
                type="button"
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  transition-colors duration-150
                "
                onClick={() => {
                  setShowPgpUpload(false);
                  setPgpPublicKey('');
                  setPgpPrivateKey('');
                  setPgpPassphrase('');
                  setPgpError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Task Summary */}
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Task Summary
        </h4>
        <div className="flex gap-8">
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl font-semibold text-neutral-800 dark:text-slate-200">
              {connection.taskCounts?.total || 0}
            </span>
            <span className="text-xs text-neutral-500 dark:text-slate-400">Total Tasks</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl font-semibold text-success-600 dark:text-success-400">
              {connection.taskCounts?.enabled || 0}
            </span>
            <span className="text-xs text-neutral-500 dark:text-slate-400">Enabled</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-2xl font-semibold text-neutral-500 dark:text-slate-400">
              {connection.taskCounts?.disabled || 0}
            </span>
            <span className="text-xs text-neutral-500 dark:text-slate-400">Disabled</span>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Timestamps
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Created
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {formatDateTime(connection.createdAt, activeTimezone(), 'full')}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Updated
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {formatDateTime(connection.updatedAt, activeTimezone(), 'full')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );


  const renderConfigurationTab = () => (
    <div className="space-y-6">
      {/* T027: HTTP/API Configuration Display */}
      {connection.transferMethod === 'api' && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            HTTP/API Configuration
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {connection.config.baseUrl && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Base URL
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100 break-all">
                  {connection.config.baseUrl}
                </span>
              </div>
            )}
            {connection.config.httpMethod && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  HTTP Method
                </label>
                <span className="
                  inline-block w-fit px-2 py-1 rounded text-xs font-medium
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-300
                ">
                  {connection.config.httpMethod}
                </span>
              </div>
            )}
            {connection.config.httpContentType && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Content Type
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100">
                  {connection.config.httpContentType}
                </span>
              </div>
            )}
            {connection.config.httpTimeout && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Timeout
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100">
                  {connection.config.httpTimeout} seconds
                </span>
              </div>
            )}
            {connection.config.httpAuthType && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Authentication Type
                </label>
                <span className="
                  inline-block w-fit px-2 py-1 rounded text-xs font-medium
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-300
                ">
                  {connection.config.httpAuthType === 'none' && 'None'}
                  {connection.config.httpAuthType === 'basic' && 'Basic Auth'}
                  {connection.config.httpAuthType === 'bearer' && 'Bearer Token'}
                  {connection.config.httpAuthType === 'oauth2_cc' && 'OAuth2 Client Credentials'}
                  {connection.config.httpAuthType === 'oauth2_custom' && 'OAuth2 Custom'}
                </span>
              </div>
            )}
            {(connection.config.httpAuthType === 'oauth2_cc' || connection.config.httpAuthType === 'oauth2_custom') && (
              <>
                {connection.config.oauth2TokenEndpoint && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                      OAuth2 Token Endpoint
                    </label>
                    <span className="text-sm text-neutral-900 dark:text-slate-100 break-all">
                      {connection.config.oauth2TokenEndpoint}
                    </span>
                  </div>
                )}
                {connection.config.oauth2ClientId && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                      Client ID
                    </label>
                    <span className="text-sm text-neutral-900 dark:text-slate-100">
                      {connection.config.oauth2ClientId}
                    </span>
                  </div>
                )}
                {connection.config.oauth2Scope && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                      OAuth2 Scope
                    </label>
                    <span className="text-sm text-neutral-900 dark:text-slate-100">
                      {connection.config.oauth2Scope}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                    Credentials
                  </label>
                  <span className="
                    inline-block w-fit px-2 py-1 rounded text-xs font-medium
                    bg-green-50 dark:bg-green-900/20
                    text-green-700 dark:text-green-400
                  ">
                    Configured
                  </span>
                </div>
              </>
            )}
            {connection.config.httpAuthType === 'basic' && (
              <>
                {connection.config.username && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                      Username
                    </label>
                    <span className="text-sm text-neutral-900 dark:text-slate-100">
                      {connection.config.username}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                    Password
                  </label>
                  <span className="
                    inline-block w-fit px-2 py-1 rounded text-xs font-medium
                    bg-green-50 dark:bg-green-900/20
                    text-green-700 dark:text-green-400
                  ">
                    Configured
                  </span>
                </div>
              </>
            )}
            {connection.config.httpAuthType === 'bearer' && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Bearer Token
                </label>
                <span className="
                  inline-block w-fit px-2 py-1 rounded text-xs font-medium
                  bg-green-50 dark:bg-green-900/20
                  text-green-700 dark:text-green-400
                ">
                  Configured
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* THUB-245: Azure Blob Configuration Display.
          Without this block the Configuration tab rendered EMPTY for an azure_blob connection —
          every other block here is gated to 'api' or 'sftp', and an Azure connection carries
          none of host/port/username/baseUrl. Users had to open Edit just to read the container
          URL. */}
      {connection.transferMethod === 'azure_blob' && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            Azure Blob Storage Configuration
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {connection.config.azureContainerUrl && (
              <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-3">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Container URL
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100 break-all">
                  {connection.config.azureContainerUrl}
                </span>
              </div>
            )}
            {/* The SAS token is write-only: entered on create/edit, stored in Secrets Manager,
                and never returned by the API. Show that it exists without implying we can
                display it — same treatment as the HTTP password/bearer fields above. */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                SAS Token
              </label>
              <span className="
                inline-block w-fit px-2 py-1 rounded text-xs font-medium
                bg-green-50 dark:bg-green-900/20
                text-green-700 dark:text-green-400
              ">
                Stored in Secrets Manager
              </span>
            </div>
          </div>
          {/* THUB-250: the path deliberately lives on the TASK, not here. Say so, or the empty
              space reads like missing configuration and invites someone to re-add a
              connection-level prefix — which is exactly what sent three brands to one path. */}
          <p className="mt-4 text-xs text-neutral-500 dark:text-slate-400 leading-relaxed">
            The connection provides the endpoint and credential. Each task supplies its own
            path via <span className="font-medium">Destination Directory</span> (outbound) or{' '}
            <span className="font-medium">Source Directory</span> (inbound), the same as SFTP —
            so several tasks can share this connection and still write to different folders.
          </p>
        </div>
      )}

      {/* Connection Settings (SFTP only) */}
      {connection.transferMethod === 'sftp' && (
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Connection Settings
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {connection.config.host && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Host
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {connection.config.host}
              </span>
            </div>
          )}
          {connection.config.port && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Port
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {connection.config.port}
              </span>
            </div>
          )}
          {connection.config.username && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Username
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {connection.config.username}
              </span>
            </div>
          )}
          {connection.config.authMethod && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Auth Method
              </label>
              <span className="
                inline-block w-fit px-2 py-1 rounded text-xs font-medium
                bg-neutral-100 dark:bg-slate-700
                text-neutral-700 dark:text-slate-300
              ">
                {connection.config.authMethod}
              </span>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Advanced Settings (SFTP only) */}
      {connection.transferMethod === 'sftp' && (
      <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
        <h4 className="
          text-base font-semibold text-neutral-800 dark:text-slate-200
          pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
        ">
          Advanced Settings
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Timeout
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.config.timeout || 30} seconds
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Retry Attempts
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.config.retryAttempts || 3}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Encryption
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.config.enableEncryption ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
              Compression
            </label>
            <span className="text-sm text-neutral-900 dark:text-slate-100">
              {connection.config.enableCompression ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>
      )}

      {/* Hosted Connection Settings (SFTP only) */}
      {connection.hostingType === 'hosted' && connection.config.publicKeyContent && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            Hosted Connection Settings
          </h4>
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Public Key
              </label>
              <pre className="
                p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all
                bg-white dark:bg-slate-900
                border border-neutral-200 dark:border-slate-700
                text-neutral-800 dark:text-slate-200
              ">
                {connection.config.publicKeyContent}
              </pre>
            </div>
            {connection.config.allowedIpRanges && connection.config.allowedIpRanges.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Allowed IP Ranges
                </label>
                <span className="text-sm text-neutral-900 dark:text-slate-100">
                  {connection.config.allowedIpRanges.join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 018-thub-5: Generated SSH Public Key Display */}
      {connection.config.generatedPublicKey && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            SSH Public Key
          </h4>
          <div className="space-y-3">
            <p className="text-sm text-neutral-600 dark:text-slate-400 m-0">
              Share this public key with your partner. They need to add it to their SFTP server's <code className="px-1.5 py-0.5 bg-neutral-100 dark:bg-slate-700 rounded text-xs">authorized_keys</code> file.
            </p>
            <pre className="
              p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all
              bg-white dark:bg-slate-900
              border border-neutral-200 dark:border-slate-700
              text-neutral-800 dark:text-slate-200
            ">
              {connection.config.generatedPublicKey}
            </pre>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className="
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium
                  bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300
                  border border-primary-200 dark:border-primary-800
                  hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors
                "
                onClick={() => {
                  navigator.clipboard.writeText(connection.config.generatedPublicKey || '');
                  toast.success('Public key copied to clipboard');
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy to Clipboard
              </button>
              {/* 018-thub-5: Regenerate Key button — only for active remote + SSH key connections */}
              <RoleGuard minimumRole="admin">
              {connection.hostingType === 'remote' &&
                connection.config.authMethod === 'key' && (
                <button
                  type="button"
                  disabled={isRotatingKey}
                  className="
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium
                    bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300
                    border border-amber-200 dark:border-amber-800
                    hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                  onClick={() => setShowRotateConfirm(true)}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {isRotatingKey ? 'Regenerating...' : 'Regenerate Key'}
                </button>
              )}
              </RoleGuard>
              {connection.config.keyGenerationMethod && (
                <span className="
                  inline-block px-2 py-1 rounded text-xs font-medium
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-600 dark:text-slate-400
                ">
                  {connection.config.keyGenerationMethod === 'auto' ? 'Auto-generated' : 'Manually provided'}
                </span>
              )}
              {/* THUB-113: Key type badge derived from public key prefix */}
              {connection.config.generatedPublicKey && (() => {
                const pk = connection.config.generatedPublicKey || '';
                let keyLabel = 'Unknown';
                if (pk.startsWith('ecdsa-sha2-nistp384')) keyLabel = 'ECDSA P-384';
                else if (pk.startsWith('ecdsa-sha2-nistp256')) keyLabel = 'ECDSA P-256';
                else if (pk.startsWith('ecdsa-sha2-nistp521')) keyLabel = 'ECDSA P-521';
                else if (pk.startsWith('ssh-rsa')) keyLabel = 'RSA';
                else if (pk.startsWith('ssh-ed25519')) keyLabel = 'Ed25519';
                return (
                  <span className="
                    inline-block px-2 py-1 rounded text-xs font-medium
                    bg-purple-100 dark:bg-purple-900/30
                    text-purple-700 dark:text-purple-300
                  ">
                    {keyLabel}
                  </span>
                );
              })()}
            </div>

            {/* 018-thub-5: Key rotation confirmation dialog */}
            {showRotateConfirm && (
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 m-0 mb-2">
                  Are you sure you want to regenerate the SSH key?
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 m-0 mb-3">
                  The previous key will be replaced. Share the new public key with your partner and confirm they have updated their authorized_keys before testing the connection.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={isRotatingKey}
                    className="
                      px-3 py-1.5 rounded text-sm font-medium
                      bg-amber-600 text-white hover:bg-amber-700
                      disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                    "
                    onClick={async () => {
                      setIsRotatingKey(true);
                      try {
                        // THUB-113: Pass stored keyType (default to ecdsa)
                        const keyType = connection.config.keyType || 'ecdsa';
                        await connectionsApi.rotateKey(connection.connectionId, keyType);
                        toast.success('SSH key pair regenerated. Share the new public key with your partner.');
                        setShowRotateConfirm(false);
                        onUpdate();
                      } catch (err: any) {
                        toast.error(err.message || 'Failed to regenerate SSH key');
                      } finally {
                        setIsRotatingKey(false);
                      }
                    }}
                  >
                    {isRotatingKey ? 'Regenerating...' : 'Confirm Regenerate'}
                  </button>
                  <button
                    type="button"
                    className="
                      px-3 py-1.5 rounded text-sm font-medium
                      bg-neutral-200 dark:bg-slate-600 text-neutral-700 dark:text-slate-200
                      hover:bg-neutral-300 dark:hover:bg-slate-500 transition-colors
                    "
                    onClick={() => setShowRotateConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AWS Resources */}
      {(connection.transferFamilyUserArn || connection.transferFamilyConnectorArn) && (
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            AWS Resources
          </h4>
          <div className="space-y-4">
            {connection.transferFamilyUserArn && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Transfer Family User ARN
                </label>
                <span className="text-xs font-mono text-neutral-600 dark:text-slate-400 break-all">
                  {connection.transferFamilyUserArn}
                </span>
              </div>
            )}
            {connection.transferFamilyConnectorArn && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                  Transfer Family Connector ARN
                </label>
                <span className="text-xs font-mono text-neutral-600 dark:text-slate-400 break-all">
                  {connection.transferFamilyConnectorArn}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Handler for test completion
  const handleTestComplete = useCallback((result: ConnectionTestResult) => {
    setTestResult(result);
    // Update connection status on successful test
    if (result.success) {
      onUpdate();
    }
  }, [onUpdate]);

  const renderHealthTab = () => {
    const displayResult = testResult || connection.connectionTestResults;

    return (
      <div className="space-y-6">
        {/* Connection Test */}
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            Connection Test
          </h4>
          <div className="mb-5">
            <RoleGuard minimumRole="operator">
              <ConnectionTestButton
                connection={connection}
                onTestComplete={handleTestComplete}
                variant="primary"
                size="medium"
                showLastResult={false}
              />
            </RoleGuard>
          </div>

          {displayResult && (
            <TestResultPanel
              result={displayResult}
              showStages={true}
              compact={false}
              connectionType={connection.hostingType === 'hosted' ? 'inbound' : 'outbound'}
              transferMethod={connection.transferMethod}
            />
          )}
        </div>

        {/* Test History Summary */}
        <div className="bg-neutral-50 dark:bg-slate-800/50 rounded-lg p-5">
          <h4 className="
            text-base font-semibold text-neutral-800 dark:text-slate-200
            pb-3 mb-4 border-b border-neutral-200 dark:border-slate-700
          ">
            Test History Summary
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Last Test
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {connection.lastConnectionTest ? formatDateTime(connection.lastConnectionTest, activeTimezone(), 'full') : 'Never'}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500 dark:text-slate-400">
                Last Successful Connection
              </label>
              <span className="text-sm text-neutral-900 dark:text-slate-100">
                {connection.lastSuccessfulConnection ? formatDateTime(connection.lastSuccessfulConnection, activeTimezone(), 'full') : 'Never'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTasksTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-neutral-800 dark:text-slate-200">
          Connection Tasks ({connectionTasks.length})
        </h4>
      </div>

      {dependenciesLoading ? (
        <div className="flex justify-center items-center py-10 text-neutral-500 dark:text-slate-400">
          Loading tasks...
        </div>
      ) : connectionTasks.length === 0 ? (
        <div className="py-8 text-center text-neutral-500 dark:text-slate-400 italic">
          <p>No tasks found for this connection.</p>
        </div>
      ) : (
        <ConnectionTaskList
          tasks={connectionTasks}
          onTaskSelect={(task) => openTaskDetail(task.taskId)}
        />
      )}
    </div>
  );

  return (
    <div
      className="
        fixed inset-0
        bg-black/50 dark:bg-black/70
        flex items-center justify-center
        z-[1000] p-4
      "
      onClick={onClose}
    >
      {/* Version Conflict Toast - temporarily disabled for debugging */}
          {/* THUB-298: the commented-out VersionConflictToast / EntityUpdateIndicator block that sat
              here is gone, along with the components themselves. The feature could not be re-enabled
              by uncommenting: its driver hook (hooks/useVersionConflict) was already deleted as
              unreachable, so the components were orphans that still shipped in the bundle via the
              shared barrel. Git history has the original if version-conflict detection is revived. */}

      <div
        className="
          bg-white dark:bg-slate-900
          rounded-xl shadow-2xl
          w-full max-w-[900px] max-h-[90vh]
          flex flex-col overflow-hidden
          border border-neutral-200 dark:border-slate-700
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="
          flex flex-shrink-0 flex-col sm:flex-row justify-between items-start gap-4
          px-6 py-5
          bg-neutral-50 dark:bg-slate-800/50
          border-b border-neutral-200 dark:border-slate-700
          rounded-t-xl
        ">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-slate-100 mb-2 truncate">
              {connection.name}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <ResourceStatusBadge
                status={toProvisioningStatus(connection.provisioningStatus)}
                size="sm"
              />
              <span className="
                px-2 py-1 rounded text-xs font-medium
                bg-primary-100 dark:bg-primary-900/30
                text-primary-700 dark:text-primary-300
              ">
                {connection.hostingType}
              </span>
              <span className="
                px-2 py-1 rounded text-xs font-medium
                bg-purple-100 dark:bg-purple-900/30
                text-purple-700 dark:text-purple-300
              ">
                {connection.transferMethod}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <RoleGuard minimumRole="admin">
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  transition-colors duration-150
                "
                onClick={() => setIsEditing(true)}
              >
                Edit
              </button>
            </RoleGuard>
            <RoleGuard minimumRole="admin">
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-danger-500 hover:bg-danger-600
                  text-white
                  transition-colors duration-150
                "
                onClick={() => setShowDeleteConfirm(true)}
                title={hasTasks ? 'Delete connection and all tasks' : 'Delete this connection'}
              >
                Delete
              </button>
            </RoleGuard>
            <button
              className="
                w-10 h-10 flex items-center justify-center
                text-2xl text-neutral-500 dark:text-slate-400
                hover:text-neutral-700 dark:hover:text-slate-200
                hover:bg-neutral-200 dark:hover:bg-slate-700
                rounded-lg transition-colors duration-150
              "
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="
          flex flex-shrink-0 border-b border-neutral-200 dark:border-slate-700
          px-6 bg-white dark:bg-slate-900
          overflow-x-auto
        ">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`
                px-5 py-3 text-sm font-medium border-b-2 transition-all duration-150 whitespace-nowrap
                ${activeTab === tab.id
                  ? 'text-primary-600 dark:text-primary-400 border-primary-500 bg-white dark:bg-slate-900'
                  : 'text-neutral-600 dark:text-slate-400 border-transparent hover:text-neutral-900 dark:hover:text-slate-200 hover:bg-neutral-50 dark:hover:bg-slate-800'
                }
              `}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`
                  ml-2 px-1.5 py-0.5 text-xs rounded-full
                  ${activeTab === tab.id
                    ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300'
                    : 'bg-neutral-100 dark:bg-slate-700 text-neutral-600 dark:text-slate-400'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-slate-900">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'configuration' && renderConfigurationTab()}
          {activeTab === 'health' && renderHealthTab()}
          {activeTab === 'history' && (
            <TestHistoryTable
              connectionId={connection.connectionId}
              connectionType={connection.hostingType === 'hosted' ? 'inbound' : 'outbound'}
              transferMethod={connection.transferMethod}
              autoRefresh={false}
            />
          )}
          {activeTab === 'tasks' && renderTasksTab()}
          {activeTab === 'audit' && (
            <AuditLogViewer
              entityType="connection"
              entityId={connection.connectionId}
              title="Connection Change History"
            />
          )}
        </div>
      </div>

      {/* Edit Connection Modal */}
      {isEditing && (
        <EditConnection
          connectionId={connection.connectionId}
          partners={partners}
          onClose={() => setIsEditing(false)}
          onUpdate={async () => { setIsEditing(false); await onUpdate(); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="
            fixed inset-0
            bg-black/60 dark:bg-black/80
            flex items-center justify-center
            z-[1100]
          "
          onClick={() => !isDeleting && setShowDeleteConfirm(false)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-lg shadow-2xl
              max-w-md w-[90%]
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-danger-500">Delete Connection</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-neutral-700 dark:text-slate-300">
                Are you sure you want to delete connection <strong className="text-neutral-900 dark:text-slate-100">{connection.name}</strong>?
              </p>
              <p className="
                p-3 rounded-lg text-sm
                bg-warning-50 dark:bg-warning-900/20
                text-warning-800 dark:text-warning-200
                border border-warning-200 dark:border-warning-800
              ">
                This action cannot be undone. Any associated AWS resources will be cleaned up.
              </p>
              {deleteError && (
                <div className="
                  p-3 rounded-lg text-sm
                  bg-danger-50 dark:bg-danger-900/20
                  text-danger-600 dark:text-danger-400
                  border border-danger-200 dark:border-danger-800
                ">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-neutral-200 dark:border-slate-700">
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-neutral-100 dark:bg-slate-700
                  text-neutral-700 dark:text-slate-200
                  hover:bg-neutral-200 dark:hover:bg-slate-600
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="
                  px-4 py-2 text-sm font-medium rounded-lg
                  bg-danger-500 hover:bg-danger-600
                  text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors duration-150
                "
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Connection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cascade Delete Dialog - shown when connection has dependencies */}
      <CascadeDeleteDialog
        isOpen={showCascadeDialog}
        onClose={() => {
          setShowCascadeDialog(false);
          setDeleteError(null);
        }}
        onConfirm={handleCascadeDelete}
        entityType="connection"
        entityName={connection.name}
        dependencies={cascadeDependencies}
        isDeleting={isDeleting}
      />

      {/* Nested Task Details Modal */}
      {selectedTaskId && (
        <div
          className="
            fixed inset-0
            bg-black/70 dark:bg-black/80
            flex items-center justify-center
            z-[1200]
          "
          onClick={closeTaskDetail}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              max-w-[800px] w-[90%] max-h-[85vh]
              overflow-y-auto
              border-2 border-primary-500 dark:border-primary-600
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* THUB-162: gate on the full-task fetch so we never render the
                stale partial list object (which flashed "still provisioning"). */}
            {taskDetailLoading || !fullTask ? (
              <div className="flex items-center justify-center p-16">
                <div className="flex flex-col items-center gap-3 text-neutral-500 dark:text-slate-400">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading task details…</span>
                </div>
              </div>
            ) : (
            <TaskDetails
              task={fullTask}
              executions={taskExecutions}
              connection={connection}
              onClose={closeTaskDetail}
              isDeleting={taskActions.isDeleting}
              deleteError={taskActions.deleteError}
              // THUB-231: all four callbacks were console.log stubs. They now
              // reach the real API via useTaskActions.
              onEdit={(task) => {
                setTaskToEdit(task);
              }}
              onDelete={async (taskId) => {
                // fullTask.version drives optimistic locking — the full task is
                // what this modal renders, so it is the authoritative version.
                await taskActions.deleteTask(taskId, fullTask.version);
                closeTaskDetail();
              }}
              onToggleStatus={async (taskId, enabled) => {
                await taskActions.toggleTaskStatus(taskId, enabled);
              }}
              onExecute={async (taskId) => {
                await taskActions.executeTask(taskId);
              }}
            />
            )}
          </div>
        </div>
      )}

      {/* THUB-231: nested Task Editor for the Edit action (mirrors TaskList). */}
      {taskToEdit && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1300] p-4"
          onClick={() => setTaskToEdit(null)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[900px] max-h-[90vh]
              overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <TaskEditor
              task={taskToEdit}
              partners={partners.map(p => ({ id: p.partnerId, name: p.name }))}
              onSave={handleUpdateTask}
              onCancel={() => setTaskToEdit(null)}
              onValidateConfiguration={handleValidateTaskConfiguration}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ConnectionDetails;
